import React, { useState } from 'react';
import { X, Plus, Save } from 'lucide-react';

const PurchaseForm = ({ supplier, editData, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    month: editData?.month || new Date().toISOString().slice(0, 7),
    amount: editData?.amount?.toString() || '',
    due: editData?.due?.toString() || '',
    pending: editData?.pending?.toString() || '',
    invoiceNumber: editData?.invoiceNumber || '',
    purchaseDate: editData?.purchaseDate || new Date().toISOString().slice(0, 10),
    description: editData?.description || ''
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.month) newErrors.month = 'Month is required';
    if (!formData.amount || formData.amount <= 0) newErrors.amount = 'Valid amount is required';
    if (formData.due < 0) newErrors.due = 'Due amount cannot be negative';
    if (formData.pending < 0) newErrors.pending = 'Pending amount cannot be negative';
    if (!formData.purchaseDate) newErrors.purchaseDate = 'Purchase date is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const purchaseData = {
        ...formData,
        amount: parseFloat(formData.amount),
        due: parseFloat(formData.due) || 0,
        pending: parseFloat(formData.pending) || 0,
        total: parseFloat(formData.amount) + parseFloat(formData.due || 0) + parseFloat(formData.pending || 0),
        supplierId: supplier.id,
        supplierName: supplier.name
      };
      onSave(purchaseData);
      onClose();
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="ui-modal-overlay">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">
                {editData ? 'Edit Purchase Record' : 'Add Purchase Record'}
              </h3>
              <p className="text-gray-600">{supplier.name}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Month *
                </label>
                <input
                  type="month"
                  value={formData.month}
                  onChange={(e) => handleChange('month', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-xl outline-none focus:ring-2 focus:ring-green-500 ${
                    errors.month ? 'border-red-500' : 'border-gray-200'
                  }`}
                />
                {errors.month && <p className="text-red-500 text-sm mt-1">{errors.month}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Purchase Date *
                </label>
                <input
                  type="date"
                  value={formData.purchaseDate}
                  onChange={(e) => handleChange('purchaseDate', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-xl outline-none focus:ring-2 focus:ring-green-500 ${
                    errors.purchaseDate ? 'border-red-500' : 'border-gray-200'
                  }`}
                />
                {errors.purchaseDate && <p className="text-red-500 text-sm mt-1">{errors.purchaseDate}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount (₹) *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-xl outline-none focus:ring-2 focus:ring-green-500 ${
                    errors.amount ? 'border-red-500' : 'border-gray-200'
                  }`}
                  placeholder="0.00"
                />
                {errors.amount && <p className="text-red-500 text-sm mt-1">{errors.amount}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Due (₹)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.due}
                  onChange={(e) => handleChange('due', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-xl outline-none focus:ring-2 focus:ring-orange-500 ${
                    errors.due ? 'border-red-500' : 'border-gray-200'
                  }`}
                  placeholder="0.00"
                />
                {errors.due && <p className="text-red-500 text-sm mt-1">{errors.due}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pending (₹)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.pending}
                  onChange={(e) => handleChange('pending', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-xl outline-none focus:ring-2 focus:ring-red-500 ${
                    errors.pending ? 'border-red-500' : 'border-gray-200'
                  }`}
                  placeholder="0.00"
                />
                {errors.pending && <p className="text-red-500 text-sm mt-1">{errors.pending}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Invoice Number
              </label>
              <input
                type="text"
                value={formData.invoiceNumber}
                onChange={(e) => handleChange('invoiceNumber', e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-green-500"
                placeholder="INV-001"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={3}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Purchase details..."
              />
            </div>

            {/* Total Preview */}
            <div className="bg-blue-50 rounded-xl p-4">
              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-700">Total Amount:</span>
                <span className="text-xl font-bold text-blue-600">
                  ₹{((parseFloat(formData.amount) || 0) + (parseFloat(formData.due) || 0) + (parseFloat(formData.pending) || 0)).toLocaleString()}
                </span>
              </div>
            </div>

            <div className="flex gap-3 pt-6 border-t">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 font-semibold"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 font-semibold flex items-center justify-center gap-2"
              >
                <Save size={18} />
                {editData ? 'Update Purchase' : 'Save Purchase'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PurchaseForm;